// src/components/chart/PerformanceChart.tsx
import React, { useId, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { Box, SxProps, Theme, Typography, useMediaQuery } from "@mui/material";
import { format, startOfDay } from "date-fns";
import { formatAxisMonth, formatSignedPercent, formatSignedPp } from "../../../utils/formatters";

import { IPerformanceDataPoint } from "../../../shared/types";
import ChartTooltip from "./ChartTooltip";
import { useAuth } from "../../../hooks/useAuth";
import { ChartCard, ChartLegend, axisTick } from "../ChartCard";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { TimeRange, TimeRangeToggle, availableRanges, sliceByRange } from "../../ui/TimeRangeToggle";
import { tokens } from "../../../theme";

/** Plotted point: the raw indexes (read by the tooltip) plus the display-only smoothed lines. */
type ChartPoint = IPerformanceDataPoint & { poolIndexSmooth: number | null; tokenIndexSmooth: number | null };

interface Props {
  data: IPerformanceDataPoint[];
  decimals: number;
  title?: string;
  subtitle?: string;
  /** `hero` renders a bare plot with legend + range toggle on one row (inside the hero card). */
  variant?: "default" | "embedded" | "hero";
  height?: number | { [key: string]: number };
  sx?: SxProps<Theme>;
  /** Legend name of the benchmark line, e.g. "HODL · FET in WETH". */
  hodlLabel?: string;
  /** Pair symbols when the series is one pair (family / bot): tooltip prices get their unit. */
  pairTokens?: [string | undefined, string | undefined];
}

const RANGE_CAPTION: Record<TimeRange, string> = {
  "1M": "Last month",
  "3M": "Last 3 months",
  "6M": "Last 6 months",
  ALL: "Since inception",
};

/**
 * Starts a window at 0%: the lines are cumulative P&L over today's capital (1,000 = 0%), so a
 * window shows the P&L made inside it, as a share of the same capital (a difference, not a ratio).
 */
const rebase = (points: IPerformanceDataPoint[]): IPerformanceDataPoint[] => {
  const first = points[0];
  if (!first || !Number.isFinite(first.poolIndex) || !Number.isFinite(first.tokenIndex)) return points;
  return points.map((p) => ({
    ...p,
    poolIndex: 1000 + (p.poolIndex - first.poolIndex),
    tokenIndex: 1000 + (p.tokenIndex - first.tokenIndex),
  }));
};

/** Axis / caption reading of a point: 1,000 ↔ 0%, 1,059 ↔ +5.9% of today's capital. */
const indexToPct = (v: number) => v / 1000 - 1;

/** Keeps ~maxPoints evenly spaced points (first and last always) so daily noise doesn't turn into a seismograph. */
const decimate = <T,>(points: T[], maxPoints: number): T[] => {
  if (points.length <= maxPoints) return points;
  const step = (points.length - 1) / (maxPoints - 1);
  return Array.from({ length: maxPoints }, (_, i) => points[Math.round(i * step)]);
};

/**
 * Display-only smoothing, applied to the daily series *before* decimation (sampling every k-th
 * day of a noisy series would alias it into a zig-zag). Centred moving average whose window
 * shrinks towards the ends, so the first (1,000) and last ("now") points stay exact. Both lines
 * get the same treatment; `poolIndex` / `tokenIndex` keep the raw values for the tooltip.
 */
const smoothSeries = (points: IPerformanceDataPoint[], window: number): ChartPoint[] => {
  const half = Math.floor(window / 2);
  const avg = (i: number, key: "poolIndex" | "tokenIndex") => {
    const h = Math.min(half, i, points.length - 1 - i);
    let sum = 0;
    let n = 0;
    for (let j = i - h; j <= i + h; j++) {
      const v = points[j][key];
      if (Number.isFinite(v)) {
        sum += v;
        n += 1;
      }
    }
    return n > 0 ? sum / n : null;
  };
  return points.map((p, i) => ({ ...p, poolIndexSmooth: avg(i, "poolIndex"), tokenIndexSmooth: avg(i, "tokenIndex") }));
};

/** Month steps for long spans: every 1, 2, 3, 6, 12 or 24 months. */
const MONTH_STEPS = [1, 2, 3, 6, 12, 24];
const monthNumber = (t: number) => {
  const d = new Date(t);
  return d.getFullYear() * 12 + d.getMonth();
};

/**
 * Regular time ticks. Long spans get month starts at one fixed step so labels are evenly
 * spaced in months (e.g. every 3 months: Dec / Mar / Jun / Sep): the smallest step that fits
 * `maxTicks`, in the phase with the most ticks, then the most centred one. Shorter spans get
 * evenly spaced days. No label within 6% of either edge (it would be clipped).
 */
const timeTicks = (from: number, to: number, maxTicks: number): { ticks: number[]; monthly: boolean } => {
  const spanDays = (to - from) / 86_400_000;
  if (spanDays > 75) {
    const margin = (to - from) * 0.06;
    const months: number[] = [];
    const d = new Date(from);
    for (let c = new Date(d.getFullYear(), d.getMonth() + 1, 1); c.getTime() < to - margin; ) {
      if (c.getTime() > from + margin) months.push(c.getTime());
      c = new Date(c.getFullYear(), c.getMonth() + 1, 1);
    }
    for (const step of MONTH_STEPS) {
      let best: number[] = [];
      let bestSkew = Infinity;
      for (let phase = 0; phase < step; phase++) {
        const ticks = months.filter((t) => monthNumber(t) % step === phase);
        if (ticks.length === 0 || ticks.length > maxTicks) continue;
        // Balance the empty stretch before the first label and after the last one.
        const skew = Math.abs(ticks[0] - from - (to - ticks[ticks.length - 1]));
        if (ticks.length > best.length || (ticks.length === best.length && skew < bestSkew)) {
          best = ticks;
          bestSkew = skew;
        }
      }
      if (best.length > 0) return { ticks: best, monthly: true };
    }
    const every = Math.max(1, Math.ceil(months.length / maxTicks));
    return { ticks: months.filter((_, i) => i % every === 0), monthly: true };
  }
  const count = Math.max(2, Math.min(maxTicks, Math.round(spanDays)));
  const step = (to - from) / count;
  return { ticks: Array.from({ length: count }, (_, i) => startOfDay(from + step * (i + 0.5)).getTime()), monthly: false };
};

/**
 * Y scale: round steps, the top snapped to the next tick, the bottom fitted to the data (a few
 * percent of padding) so a series sitting on the 1,000 base doesn't leave a whole empty band
 * (and a "980" label) under it.
 */
const niceScale = (values: number[]): { ticks: number[]; domain: [number, number]; step: number } => {
  if (values.length === 0) return { ticks: [1000], domain: [990, 1010], step: 10 };
  const min = Math.min(1000, ...values);
  const max = Math.max(1000, ...values);
  const span = Math.max(max - min, 1);
  const raw = span / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  // 2.5 only where it stays a whole number (25, 250…): "1,002.5" would print as an uneven "1,003".
  const multipliers = mag >= 10 ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10];
  const step = multipliers.map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const floorTick = Math.floor(min / step) * step;
  const bottom = Math.max(floorTick, min - span * 0.04);
  const top = Math.ceil(max / step - 0.001) * step;
  const ticks: number[] = [];
  for (let t = Math.ceil(bottom / step - 0.001) * step; t <= top + step * 0.001; t += step) ticks.push(Number(t.toFixed(4)));
  return { ticks, domain: [bottom, Math.max(top, max)], step };
};

const PerformanceChart: React.FC<Props> = ({
  data,
  decimals,
  title = "Performance",
  subtitle,
  variant = "default",
  height,
  sx,
  hodlLabel = "HODL",
  pairTokens,
}) => {
  const { groups } = useAuth();
  const isAdmin = groups.includes("admin");
  const gradientId = `lp-${useId().replace(/:/g, "")}`;
  const ranges = useMemo(() => availableRanges(data[0]?.date), [data]);
  const [range, setRange] = useState<TimeRange>("ALL");
  const activeRange = ranges.includes(range) ? range : "ALL";

  const isPhone = useMediaQuery((t: Theme) => t.breakpoints.down("sm"));
  const visible = useMemo(() => {
    const sliced = sliceByRange(data, activeRange, (d) => d.date);
    const based = activeRange === "ALL" ? sliced : rebase(sliced);
    const maxPoints = variant === "hero" || isPhone ? 70 : 110;
    // Window proportional to the span (odd): ~15 days over 300, 3 days over a 1M window.
    const window = Math.max(3, Math.round(based.length / 40) * 2 + 1);
    return decimate(smoothSeries(based, window), maxPoints);
  }, [data, activeRange, variant, isPhone]);

  const timeDomain = useMemo<[number, number]>(() => {
    if (visible.length < 2) {
      const now = Date.now();
      return [now - 86400000, now];
    }
    return [startOfDay(visible[0].date).getTime(), visible[visible.length - 1].date];
  }, [visible]);

  const yScale = useMemo(
    () =>
      niceScale(
        // Fit what is drawn: both lines are the smoothed series.
        visible
          .flatMap((d) => (isAdmin ? [d.poolIndexSmooth, d.tokenIndexSmooth] : [d.poolIndexSmooth]))
          .filter((v): v is number => v != null && Number.isFinite(v))
      ),
    [visible, isAdmin]
  );

  // Ticks in %: a 20-point step is 2%; finer steps get the decimals they need.
  const formatYTick = (t: number) => {
    const pct = indexToPct(Number(t)) * 100;
    const decimals = yScale.step >= 10 ? 0 : yScale.step >= 1 ? 1 : 2;
    const body = Math.abs(pct).toFixed(decimals);
    return Number(body) === 0 ? "0%" : `${pct > 0 ? "+" : "\u2212"}${body}%`;
  };
  // Wide enough for the longest label ("+100%", "−12.5%") so the sign is never clipped.
  const yAxisWidth = Math.ceil(Math.max(44, 14 + 7.5 * Math.max(...yScale.ticks.map((t) => formatYTick(t).length))));

  const xTicks = useMemo(
    () => timeTicks(timeDomain[0], timeDomain[1], isPhone ? 4 : 6),
    [timeDomain, isPhone]
  );
  const tickFormat = (t: number) => (xTicks.monthly ? formatAxisMonth(t) : format(new Date(t), "MMM d"));

  const legend = [
    { label: "LP strategy", color: tokens.chart.lp },
    ...(isAdmin ? [{ label: hodlLabel, color: tokens.chart.hodl }] : []),
  ];

  // A window re-bases both lines to 1,000 at its start: state the window's own result, since the
  // hero chips and the card subtitle are "since inception".
  const lastVisible = visible[visible.length - 1];
  const windowCaption =
    activeRange !== "ALL" && lastVisible
      ? [
          RANGE_CAPTION[activeRange],
          formatSignedPercent(indexToPct(lastVisible.poolIndex)),
          ...(isAdmin && lastVisible.tokenIndex
            ? [`${formatSignedPp((lastVisible.poolIndex - lastVisible.tokenIndex) / 1000)} vs HODL`]
            : []),
        ].join(" · ")
      : null;
  const shownSubtitle = windowCaption ?? subtitle;
  const toggle = <TimeRangeToggle value={activeRange} onChange={setRange} options={ranges} />;

  const plot = (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={visible} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tokens.gold} stopOpacity={0.34} />
            <stop offset="100%" stopColor={tokens.gold} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={tokens.chart.grid} />
        <XAxis
          dataKey="date"
          type="number"
          scale="time"
          domain={timeDomain}
          tickFormatter={tickFormat}
          ticks={xTicks.ticks}
          tick={axisTick}
          axisLine={false}
          tickLine={false}
          dy={8}
          allowDataOverflow
          interval={0}
        />
        <YAxis
          tickFormatter={formatYTick}
          tick={axisTick}
          axisLine={false}
          tickLine={false}
          domain={yScale.domain}
          ticks={yScale.ticks}
          width={yAxisWidth}
        />
        <ReferenceLine y={1000} stroke="rgba(255,255,255,0.18)" strokeDasharray="3 4" />
        {/* Phones: a fixed readout at the top-left of the plot instead of a floating card that covers the touch point and clips. */}
        <Tooltip
          content={<ChartTooltip decimals={decimals} compact={isPhone} pairTokens={pairTokens} />}
          cursor={{ stroke: "rgba(255,255,255,0.24)", strokeWidth: 1 }}
          position={isPhone ? { x: 8, y: 0 } : undefined}
          allowEscapeViewBox={isPhone ? { x: false, y: false } : undefined}
        />
        {isAdmin && (
          <Line
            connectNulls
            type="monotoneX"
            dataKey="tokenIndexSmooth"
            name="HODL"
            stroke={tokens.chart.hodl}
            strokeWidth={1.25}
            strokeOpacity={0.6}
            dot={false}
            activeDot={{ r: 4, stroke: "#000", strokeWidth: 2, fill: "#BDBDBD" }}
            isAnimationActive={false}
          />
        )}
        <Area
          connectNulls
          type="monotoneX"
          dataKey="poolIndexSmooth"
          name="LP"
          className="lp-series"
          stroke={tokens.chart.lp}
          strokeWidth={2.25}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 5, stroke: "#000", strokeWidth: 2, fill: tokens.chart.lp }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const glowSx = {
    "& .lp-series .recharts-area-curve": { filter: "drop-shadow(0 0 6px rgba(241, 211, 175, 0.55))" },
  };

  if (variant === "hero") {
    return (
      <Box sx={{ minWidth: 0, ...((sx as object) ?? {}) }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
          <Typography noWrap sx={{ fontSize: "0.75rem", color: "text.secondary", minWidth: 0 }}>
            {/* Phones: the window's result gets its own line (it would be cut beside the toggle). */}
            {(!isPhone && windowCaption) || (isPhone ? "P&L · % of capital" : "Cumulative P&L · % of today's capital")}
          </Typography>
          {toggle}
        </Box>
        {isPhone && windowCaption && (
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", mt: -0.5, mb: 1.5 }}>
            {windowCaption}
          </Typography>
        )}
        <Box sx={{ height: height ?? { xs: 180, md: 236 }, ml: -1, ...glowSx }}>{plot}</Box>
        <Box sx={{ mt: 1.5, pl: 5.5 }}>
          <ChartLegend items={legend} />
        </Box>
      </Box>
    );
  }

  const cardSx = [glowSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>;

  // Phones: hero pattern (range toggle top-right, legend under the plot) instead of ChartCard's
  // stacked title / legend / toggle header. The title keeps the full width (beside the toggle a
  // long one wraps to three lines); the subtitle shares the toggle's row.
  if (isPhone) {
    const body = (
      <>
        <Box sx={{ mb: 1.5 }}>
          <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {(subtitle || ranges.length > 1) && (
            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ minWidth: 0, textWrap: "balance" }}
              >
                {subtitle}
              </Typography>
              <Box sx={{ flexShrink: 0 }}>{toggle}</Box>
            </Box>
          )}
          {windowCaption && (
            <Typography sx={{ mt: 1, fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }}>
              {windowCaption}
            </Typography>
          )}
        </Box>
        {/* ChartCard's phone plot height unless the caller sets one. */}
        <Box sx={{ height: height ?? 240, width: "100%", position: "relative", ml: -1 }}>{plot}</Box>
        <Box sx={{ mt: 1.5, pl: 5.5 }}>
          <ChartLegend items={legend} />
        </Box>
      </>
    );
    return variant === "embedded" ? <Box sx={cardSx}>{body}</Box> : <SurfaceCard sx={cardSx}>{body}</SurfaceCard>;
  }

  return (
    <ChartCard
      title={title}
      subtitle={shownSubtitle}
      legend={legend}
      action={toggle}
      bare={variant === "embedded"}
      height={height}
      sx={cardSx}
    >
      {plot}
    </ChartCard>
  );
};

export default PerformanceChart;
